import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Plus } from "lucide-react";
import * as Icons from '../../ui/shared/Icons';
import { supabase } from "@/lib/supabase";

interface QAPair {
  id: string;
  question: string;
  answer: string;
  assistant_id: string;
  created_at: string;
  updated_at: string;
}

const KnowledgeBaseTab: React.FC = () => {
  const { id: assistantId } = useParams<{ id: string }>();
  const [qaPairs, setQaPairs] = useState<QAPair[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");

  useEffect(() => {
    if (assistantId) {
      loadQAPairs();
    }
  }, [assistantId]);

  const loadQAPairs = async () => {
    if (!assistantId || !supabase) return;

    try {
      const { data, error } = await supabase
        .from('account_assistants_qa')
        .select('*')
        .eq('assistant_id', assistantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQaPairs(data || []);
    } catch (error) {
      console.error('Erro ao carregar perguntas:', error);
      showMessage('error', 'Erro ao carregar perguntas');
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleAdd = async () => {
    if (!newQuestion.trim() || !newAnswer.trim()) {
      showMessage('error', 'Pergunta e resposta são obrigatórias');
      return;
    }

    if (!assistantId || !supabase) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('account_assistants_qa')
        .insert({
          assistant_id: assistantId,
          question: newQuestion.trim(),
          answer: newAnswer.trim()
        });

      if (error) throw error;

      showMessage('success', 'Pergunta adicionada com sucesso!');
      setNewQuestion("");
      setNewAnswer("");
      await loadQAPairs();
    } catch (error) {
      console.error('Erro ao adicionar pergunta:', error);
      showMessage('error', 'Erro ao adicionar pergunta');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (qa: QAPair) => {
    setEditingId(qa.id);
    setNewQuestion(qa.question);
    setNewAnswer(qa.answer);
  };

  const handleUpdate = async () => {
    if (!editingId || !supabase) return;
    if (!newQuestion.trim() || !newAnswer.trim()) {
      showMessage('error', 'Pergunta e resposta são obrigatórias');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('account_assistants_qa')
        .update({
          question: newQuestion.trim(),
          answer: newAnswer.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', editingId);

      if (error) throw error;

      showMessage('success', 'Pergunta atualizada com sucesso!');
      setEditingId(null);
      setNewQuestion("");
      setNewAnswer("");
      await loadQAPairs();
    } catch (error) {
      console.error('Erro ao atualizar pergunta:', error);
      showMessage('error', 'Erro ao atualizar pergunta');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Deseja realmente remover esta pergunta?')) return;
    if (!supabase) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('account_assistants_qa')
        .delete()
        .eq('id', id);

      if (error) throw error;

      showMessage('success', 'Pergunta removida com sucesso!');
      await loadQAPairs();
    } catch (error) {
      console.error('Erro ao remover pergunta:', error);
      showMessage('error', 'Erro ao remover pergunta');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setNewQuestion("");
    setNewAnswer("");
  };

  return (
    <div className="w-full flex pb-8 flex-col gap-4">
      {message && (
        <div className={`p-3 rounded-xl border ${
          message.type === 'success'
            ? 'bg-[#ECFDF5] border-green-200 text-[#16A34A] dark:bg-green-500/10 dark:border-green-500/30 dark:text-[#22C55E]'
            : 'bg-[#FEF2F2] border-red-200 text-[#DC2626] dark:bg-red-500/10 dark:border-red-500/30 dark:text-[#F87171]'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex w-full flex-col items-start gap-3">
        <Label htmlFor="question" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">
          {editingId ? 'Editar Pergunta' : 'Nova Pergunta'}
        </Label>
        <Input
          placeholder="Digite a pergunta"
          id="question"
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          className="h-11 !h-[44px] rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] !w-full text-[14px]"
          type="text"
        />
        <Textarea
          placeholder="Digite a resposta"
          value={newAnswer}
          onChange={(e) => setNewAnswer(e.target.value)}
          className="!h-[100px]"
        />

        <div className="flex gap-2">
          {editingId ? (
            <>
              <Button
                onClick={handleUpdate}
                disabled={loading}
                className="flex items-center gap-2"
              >
                {loading ? 'Atualizando...' : 'Atualizar'}
              </Button>
              <Button
                onClick={handleCancelEdit}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
              >
                Cancelar
              </Button>
            </>
          ) : (
            <Button
              onClick={handleAdd}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <Plus size={16} />
              {loading ? 'Adicionando...' : 'Adicionar'}
            </Button>
          )}
        </div>
      </div>

      {qaPairs.length > 0 ? (
        <Accordion
          type="single"
          collapsible
          className="w-full flex flex-col gap-3"
        >
          {qaPairs.map((qa) => (
            <AccordionItem key={qa.id} value={qa.id}>
              <AccordionTrigger>{qa.question}</AccordionTrigger>
              <AccordionContent className="flex flex-col gap-4 text-balance">
                <p>{qa.answer}</p>
                <div className="flex mt-4 justify-start items-center gap-2">
                  <button
                    onClick={() => handleEdit(qa)}
                    disabled={loading}
                    className="hover:opacity-80 disabled:opacity-50"
                  >
                    <Icons.edit className="w-[18px] h-[18px]"/>
                  </button>
                  <button
                    onClick={() => handleDelete(qa.id)}
                    disabled={loading}
                    className="hover:opacity-80 disabled:opacity-50"
                  >
                    <Icons.delte className="w-[18px] h-[18px]"/>
                  </button>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="text-center py-8 text-[#64748B] dark:text-[#71717A]">
          <p>Nenhuma pergunta cadastrada ainda</p>
          <p className="text-sm mt-2">Adicione perguntas e respostas para construir a base de conhecimento</p>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseTab;
